import {
    Check,
    Heart,
    PackageCheck,
    RotateCcw,
    ShoppingCart,
    Star,
    Truck,
    Zap,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import "./CartYourItems.css";
import { CartItem } from "../../../../Redux/custmoer_cart_service/types";
import { useCart } from "../../../../context/CartContext";
import { useWishlist } from "../../../../context/WishlistContext";
import { useAppDispatch, useAppSelector } from "../../../../Redux/hooks";
import { fetchMyOrders } from "../../../../Redux/custmoer_orders/slice";
import { RootState } from "../../../../store";
import {
    fetchCatalogMap,
    mapWishlistToYourItemsProduct,
    mapOrderToYourItemsProduct,
    mapSavedCartItemToProduct,
    YourItemsProduct,
    toNumericId,
} from "./CartYourItemsData";

export type { YourItemsProduct };

type YourItemsTab = "saved" | "again" | "favourites";

type ActionState = Record<number, boolean>;

type CartYourItemsProps = {
    savedItems?: CartItem[];
};

const formatPrice = (value: number) =>
    new Intl.NumberFormat(
        "en-IN"
    ).format(Math.max(0, Math.round(value)));

const CartYourItems = ({
    savedItems,
}: CartYourItemsProps) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { addToCart, moveSavedItemToCart, removeSavedItem } = useCart();
    const { wishlist, toggleWishlist, isItemWishlisted, removeItemFromWishlist } = useWishlist();

    const { orders } = useAppSelector(
        (state: RootState) => state.customerOrders
    );

    const [activeTab, setActiveTab] = useState<YourItemsTab>(
        savedItems && savedItems.length > 0 ? "saved" : "again"
    );
    const [staticAdded, setStaticAdded] = useState<ActionState>({});
    const [selectedColors, setSelectedColors] = useState<Record<string, string>>({});
    const [dismissedAgainIds, setDismissedAgainIds] = useState<Set<number>>(new Set());
    const [catalogMap, setCatalogMap] = useState<Map<string, any>>(new Map());

    // Fetch catalog products to enrich items with live price, mrp, colors & stock details (same as WishlistPage)
    useEffect(() => {
        let cancelled = false;
        fetchCatalogMap().then((map) => {
            if (!cancelled && map.size > 0) {
                setCatalogMap(map);
            }
        });
        return () => {
            cancelled = true;
        };
    }, []);

    // Fetch past orders on mount for "Buy it again" tab
    useEffect(() => {
        dispatch(fetchMyOrders({ page: 1, limit: 30 }));
    }, [dispatch]);

    // Extract unique products from past customer orders enriched via catalogMap
    const againItems: YourItemsProduct[] = useMemo(() => {
        if (!orders || orders.length === 0) return [];
        const seen = new Set<string>();
        const list: YourItemsProduct[] = [];

        orders.forEach((order) => {
            (order.productDetails || []).forEach((prod: any) => {
                if (prod.productId && !seen.has(prod.productId)) {
                    seen.add(prod.productId);
                    const numericId = toNumericId(prod.productId);
                    if (dismissedAgainIds.has(numericId)) return;

                    list.push(mapOrderToYourItemsProduct(prod, order, catalogMap));
                }
            });
        });

        return list;
    }, [orders, catalogMap, dismissedAgainIds]);

    // Transform wishlist into product card format using catalog enrichment (identical to WishlistPage)
    const favouriteItems: YourItemsProduct[] = useMemo(() => {
        if (!wishlist || wishlist.length === 0) return [];
        return wishlist.map((item: any, idx: number) =>
            mapWishlistToYourItemsProduct(item, catalogMap, idx)
        );
    }, [wishlist, catalogMap]);

    // Transform saved cart items into product card format
    const savedProducts: YourItemsProduct[] = useMemo(() => {
        if (!savedItems || savedItems.length === 0) return [];
        return savedItems.map((item) => mapSavedCartItemToProduct(item, catalogMap));
    }, [savedItems, catalogMap]);

    const getColorSelectionKey = (product: YourItemsProduct) =>
        `${activeTab}-${product.id}`;

    const getSelectedColor = (product: YourItemsProduct) => {
        const selectionKey = getColorSelectionKey(product);
        return (
            selectedColors[selectionKey] ||
            product.colors?.[0]?.name ||
            ""
        );
    };

    const handleColorSelect = (
        product: YourItemsProduct,
        colorName: string
    ) => {
        const selectionKey = getColorSelectionKey(product);
        setSelectedColors((prev) => ({
            ...prev,
            [selectionKey]: colorName,
        }));
    };

    const handleAddProduct = (product: YourItemsProduct) => {
        if (staticAdded[product.id]) return;

        addToCart({
            productId: product.productIdStr || String(product.id),
            title: product.title,
            description: product.description || product.title,
            image: product.image,
            price: product.price || 0,
            mrp: product.mrp || product.price || 0,
            color: getSelectedColor(product) || undefined,
        });

        setStaticAdded((prev) => ({ ...prev, [product.id]: true }));
    };

    const handleMoveSavedToCart = async (item: CartItem) => {
        try {
            await moveSavedItemToCart(item.id);
            toast.success("Item moved to cart");
        } catch (err: any) {
            toast.error(err || "Failed to move item to cart");
        }
    };

    const handleDeleteSaved = async (item: CartItem) => {
        try {
            await removeSavedItem(item.id);
            toast.success("Saved item removed");
        } catch (err: any) {
            toast.error(err || "Failed to remove saved item");
        }
        setSelectedColors((prev) => {
            const next = { ...prev };
            const product = savedProducts.find((p) => p.productIdStr === item.productId);
            if (product) {
                delete next[`saved-${product.id}`];
            }
            return next;
        });
    };

    const handleDeleteProduct = (product: YourItemsProduct) => {
        if (activeTab === "again") {
            setDismissedAgainIds((prev) => new Set([...prev, product.id]));
            return;
        }

        if (activeTab === "favourites") {
            if (product.productIdStr) {
                removeItemFromWishlist(product.productIdStr);
            } else {
                toggleWishlist(String(product.id));
            }
        }
    };

    const handleSeeSimilar = (query?: string) => {
        if (query) {
            navigate("/SearchPage?q=" + encodeURIComponent(query));
        } else {
            navigate("/HomePage");
        }
    };

    const handleProductClick = (product: YourItemsProduct) => {
        const target = product.productIdStr || product.id;
        navigate(`/product/${target}`);
    };

    const renderProductCard = (product: YourItemsProduct, savedCartItem?: CartItem) => {
        const hasPrice = typeof product.price === "number" && product.price > 0;
        const hasMrp = typeof product.mrp === "number" && product.mrp > 0;
        const discount =
            hasPrice && hasMrp && product.mrp! > product.price!
                ? Math.round(((product.mrp! - product.price!) / product.mrp!) * 100)
                : 0;

        const completed = savedCartItem
            ? false
            : Boolean(staticAdded[product.id]);
        const selectedColor = getSelectedColor(product);
        const pId = product.productIdStr || String(product.id);
        const isWish = isItemWishlisted(pId) || activeTab === "favourites";

        return (
            <article
                className="your-items-product-card"
                key={savedCartItem ? `saved-${savedCartItem.id}` : `${activeTab}-${product.id}`}
            >
                <div className="your-items-product-image-wrapper">
                    {product.badge && (
                        <span className="your-items-product-badge">
                            {product.badge}
                        </span>
                    )}

                    <button
                        type="button"
                        className={`your-items-product-wishlist ${
                            isWish ? "cart-wishlist-active" : ""
                        }`}
                        aria-label="Toggle wishlist"
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleWishlist(pId);
                        }}
                    >
                        <Heart
                            size={18}
                            strokeWidth={2}
                            fill={isWish ? "currentColor" : "none"}
                        />
                    </button>

                    {product.image ? (
                        <img
                            src={product.image}
                            alt={product.title}
                            className="your-items-product-image"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleProductClick(product)}
                        />
                    ) : (
                        <div
                            className="your-items-product-image"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "#f1f5f9",
                                color: "#94a3b8",
                                cursor: "pointer",
                            }}
                            onClick={() => handleProductClick(product)}
                        >
                            No Image
                        </div>
                    )}
                </div>

                <div className="your-items-product-info">
                    {product.colors && product.colors.length > 0 && (
                        <div className="your-items-product-colors">
                            <div className="your-items-product-color-list">
                                {product.colors.slice(0, 4).map((color) => {
                                    const isColorSelected = selectedColor === color.name;
                                    return (
                                        <button
                                            type="button"
                                            className={`your-items-product-color ${
                                                isColorSelected ? "cart-color-selected" : ""
                                            }`}
                                            key={color.name}
                                            title={color.name}
                                            aria-label={`Select ${color.name}`}
                                            aria-pressed={isColorSelected}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleColorSelect(product, color.name);
                                            }}
                                        >
                                            <span style={{ backgroundColor: color.value }} />
                                        </button>
                                    );
                                })}
                            </div>

                            {product.extraColors && product.extraColors > 0 ? (
                                <span
                                    className="your-items-product-more-colors"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => handleProductClick(product)}
                                >
                                    +{product.extraColors} more
                                </span>
                            ) : null}
                        </div>
                    )}

                    <h3
                        style={{ cursor: "pointer" }}
                        onClick={() => handleProductClick(product)}
                    >
                        {product.title}
                    </h3>

                    {product.description && (
                        <p className="your-items-product-description">
                            {product.description}
                        </p>
                    )}

                    {product.rating && product.rating > 0 ? (
                        <div className="your-items-product-rating">
                            <strong>{product.rating.toFixed(1)}</strong>
                            <span className="your-items-product-stars">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <Star
                                        key={star}
                                        size={11}
                                        strokeWidth={1.8}
                                        fill={
                                            star <= Math.round(product.rating!)
                                                ? "currentColor"
                                                : "none"
                                        }
                                    />
                                ))}
                            </span>
                            {product.reviews && (
                                <span className="your-items-product-review-count">
                                    ({product.reviews})
                                </span>
                            )}
                        </div>
                    ) : null}

                    {product.deal && (
                        <div className={`your-items-product-deal ${product.dealType || "special"}`}>
                            <span />
                            <strong>{product.deal}</strong>
                        </div>
                    )}

                    {hasPrice && (
                        <div className="your-items-product-pricing">
                            <div className="your-items-product-price-main">
                                <strong>
                                    <sup>₹</sup>
                                    {formatPrice(product.price!)}
                                </strong>
                                {discount > 0 && <span>{discount}% OFF</span>}
                            </div>

                            {hasMrp && product.mrp! > product.price! && (
                                <div className="your-items-product-price-secondary">
                                    <span>MRP</span>
                                    <del>₹{formatPrice(product.mrp!)}</del>
                                    <strong>Save ₹{formatPrice(product.mrp! - product.price!)}</strong>
                                </div>
                            )}
                        </div>
                    )}

                    {product.delivery && (
                        <div className={`your-items-product-delivery ${product.deliveryType || "delivery"}`}>
                            {product.deliveryType === "fast" ? (
                                <Zap size={13} strokeWidth={2} />
                            ) : product.deliveryType === "stock" ? (
                                <PackageCheck size={13} strokeWidth={1.9} />
                            ) : (
                                <Truck size={13} strokeWidth={1.9} />
                            )}
                            <span>{product.delivery}</span>
                        </div>
                    )}

                    <button
                        type="button"
                        className={`your-items-product-cart-button ${
                            completed ? "cart-btn-completed" : ""
                        }`}
                        onClick={() =>
                            savedCartItem
                                ? handleMoveSavedToCart(savedCartItem)
                                : handleAddProduct(product)
                        }
                        disabled={completed}
                    >
                        {completed ? (
                            <>
                                <Check size={15} strokeWidth={2.4} />
                                {savedCartItem ? "Moved to Cart" : "Added to Cart"}
                            </>
                        ) : savedCartItem ? (
                            "Move to Cart"
                        ) : (
                            <>
                                <ShoppingCart size={15} strokeWidth={1.9} />
                                Add to Cart
                            </>
                        )}
                    </button>

                    <div className="your-items-product-links">
                        <button
                            type="button"
                            onClick={() =>
                                savedCartItem ? handleDeleteSaved(savedCartItem) : handleDeleteProduct(product)
                            }
                        >
                            Delete
                        </button>
                        <span />
                        <button
                            type="button"
                            onClick={() => handleSeeSimilar(product.category)}
                        >
                            See more like this
                        </button>
                    </div>
                </div>
            </article>
        );
    };

    return (
        <section className="cart-your-items">
            <div className="cart-your-items-header">
                <span>YOUR ITEMS</span>
                <h2>Your Items</h2>
            </div>

            <div className="cart-your-items-tabs">
                <button
                    type="button"
                    className={activeTab === "saved" ? "cart-tab-active" : ""}
                    onClick={() => setActiveTab("saved")}
                >
                    Saved
                    <span>{savedProducts.length}</span>
                </button>

                <button
                    type="button"
                    className={activeTab === "again" ? "cart-tab-active" : ""}
                    onClick={() => setActiveTab("again")}
                >
                    Buy it again
                    <span>{againItems.length}</span>
                </button>

                <button
                    type="button"
                    className={activeTab === "favourites" ? "cart-tab-active" : ""}
                    onClick={() => setActiveTab("favourites")}
                >
                    Your Favourites
                    <span>{favouriteItems.length}</span>
                </button>
            </div>

            {activeTab === "saved" ? (
                savedProducts.length > 0 ? (
                    <div className="cart-your-items-grid">
                        {savedProducts.map((product, idx) =>
                            renderProductCard(product, savedItems?.[idx])
                        )}
                    </div>
                ) : (
                    <div className="cart-your-items-empty">
                        <Heart size={25} strokeWidth={1.5} />
                        <strong>No saved items yet</strong>
                        <p>Products you save for later will appear here.</p>
                    </div>
                )
            ) : activeTab === "again" ? (
                againItems.length > 0 ? (
                    <div className="cart-your-items-grid">
                        {againItems.map((product) => renderProductCard(product))}
                    </div>
                ) : (
                    <div className="cart-your-items-empty">
                        <RotateCcw size={25} strokeWidth={1.5} />
                        <strong>No past orders yet</strong>
                        <p>Items you purchase will appear here so you can easily re-order them.</p>
                    </div>
                )
            ) : favouriteItems.length > 0 ? (
                <div className="cart-your-items-grid">
                    {favouriteItems.map((product) => renderProductCard(product))}
                </div>
            ) : (
                <div className="cart-your-items-empty">
                    <Heart size={25} strokeWidth={1.5} />
                    <strong>No favourites yet</strong>
                    <p>Click the heart icon on any product to save it to your favourites.</p>
                </div>
            )}
        </section>
    );
};

export default CartYourItems;
